import React, { useEffect, useMemo, useRef } from "react";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Stepper from "react-stepper-horizontal";
import { observer } from "mobx-react";
import { Box, Stack } from "@chakra-ui/react";
import { useStore } from "@/store";
import { translations } from "@/constants/translations";
import { Outlet, useRouterState } from "@tanstack/react-router";
import { renderInnerHtml } from "@/helpers/dom";
import { H1 } from "@/components/Heading";
import { telemetry } from "@/telemetry";
import type { OrderStep } from "@/telemetry/events";

const orderSteps: OrderStep[] = ["details", "shipping", "summary", "payment"];

export const OrderPage = observer(() => {
  const { location } = useRouterState();
  const { activeLanguage, activeIndigenousCommunity, orderData } = useStore();
  const language = translations.pages["order-details"][activeLanguage];
  const step = orderSteps.find((name) => location.pathname.includes(name));
  const stepTimer = useRef(telemetry.startTimer());
  const hasAbandonedStep = useRef(false);

  useEffect(() => {
    if (!step) return;

    stepTimer.current = telemetry.startTimer();
    hasAbandonedStep.current = false;
    telemetry.trackEvent("order_step_viewed", { step });
  }, [step]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState !== "hidden" ||
        !step ||
        hasAbandonedStep.current
      )
        return;

      hasAbandonedStep.current = true;
      telemetry.trackEvent("funnel_abandoned", {
        last_step: step,
        seconds_on_step: Math.round(stepTimer.current() / 1000),
        products_chosen: orderData.products.some(
          (product) => product.quantity > 0,
        ),
      });
      telemetry.flush();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [step, orderData]);

  const activeStep = useMemo(() => {
    const path = location.pathname;

    let decrement = activeIndigenousCommunity ? 1 : 0;

    if (path.includes("details")) return 0;
    else if (path.includes("shipping")) return 1 - decrement;
    else if (path.includes("summary")) return 2 - decrement;
    else if (path.includes("payment")) return 3 - decrement;

    return 0;
  }, [location, activeIndigenousCommunity]);

  const steps = useMemo(() => {
    const items = [
      {
        title: renderInnerHtml(language.order_details),
        icon: "/images/order-details-ellipse.png",
      },
      {
        title: renderInnerHtml(language.shipping_detail),
        icon: "/images/shipping-details-ellipse.png",
      },
      {
        title: renderInnerHtml(language.summary),
        icon: "/images/summary-ellipse.png",
      },
      {
        title: renderInnerHtml(language.payment),
        icon: "/images/payment-ellipse.png",
      },
    ];

    // Indigenous flow has no shipping details
    if (activeIndigenousCommunity) items.splice(1, 1);

    return items;
  }, [activeIndigenousCommunity, language]);

  return (
    <Stack spacing="14">
      <Box>
        {activeIndigenousCommunity && (
          <H1
            textAlign="center"
            pb="4"
          >
            {activeIndigenousCommunity.name}
          </H1>
        )}
        <Stepper
          steps={steps}
          activeStep={activeStep}
          defaultTitleColor="white"
          activeTitleColor="white"
          completeTitleColor="white"
          circleFontColor="black"
          barStyle="solid"
          defaultColor="#DCDDDE"
          size={50}
          defaultBarColor="white"
          defaultBorderColor="white"
          defaultBorderWidth={5}
          circleFontSize={0}
          lineMarginOffset={0}
          activeColor="var(--chakra-colors-primary)"
          completeColor="var(--chakra-colors-primary)"
          completeBarColor="var(--chakra-colors-primary)"
        />
      </Box>

      <Outlet />
    </Stack>
  );
});
